import type { ReactNode } from 'react'
import { createContext, useContext, useMemo, useState } from 'react'

import { ApiError } from '../lib/api'
import { DeepLinkConfigContext, configFromServer } from '../lib/deeplinks'
import { useAppConfigQuery, useMe } from '../lib/queries'
import { Card } from '../ui/Card'
import { Icon } from '../ui/Icon'
import { ToastZone, useToasts, type ToastTono } from '../ui/Toast'
import { DemoDataBanner } from './DemoDataBanner'
import { Header } from './Header'
import { ShareModeBanner } from './ShareModeBanner'
import { Sidebar } from './Sidebar'
import { currentPeriod, lastPeriods } from './periods'

const PERIODOS_EN_SELECTOR = 12

interface PeriodoContextValue {
  periodo: string
  setPeriodo: (periodo: string) => void
}

const PeriodoContext = createContext<PeriodoContextValue | null>(null)

/** Período seleccionado en el header. Lo consumen las seis pantallas. */
export function usePeriodo(): PeriodoContextValue {
  const ctx = useContext(PeriodoContext)
  if (ctx === null) throw new Error('usePeriodo debe usarse dentro de AppShell')
  return ctx
}

interface ToastContextValue {
  push: (tono: ToastTono, texto: string) => string
}

const ToastContext = createContext<ToastContextValue | null>(null)

/** Avisos de resultado desde cualquier pantalla. */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (ctx === null) throw new Error('useToast debe usarse dentro de AppShell')
  return ctx
}

export interface AppShellProps {
  children: ReactNode
  /** Título de la vista actual. Lo pone el router. */
  titulo?: string
  subtitulo?: ReactNode
  /** Contadores de la sidebar por ruta. */
  conteos?: Readonly<Record<string, number>>
}

/**
 * Layout del panel: sidebar + header + banner de datos dummy + contenido, más
 * los dos contextos que atraviesan las pantallas (período y avisos) y la config
 * de deep links que llega del servidor.
 *
 * Bloquea el render hasta resolver `/api/me`: sin saber el rol no se puede ni
 * dibujar la navegación, y adivinar mientras carga muestra links que después
 * desaparecen.
 */
export function AppShell({
  children,
  titulo,
  subtitulo,
  conteos,
}: AppShellProps) {
  const [ahora] = useState(() => new Date())
  const [periodo, setPeriodo] = useState(() => currentPeriod(ahora))
  const periodos = useMemo(
    () => lastPeriods(ahora, PERIODOS_EN_SELECTOR),
    [ahora],
  )

  const { data: user, isLoading, error } = useMe()
  const { data: config } = useAppConfigQuery()
  const toasts = useToasts()

  const deepLinkConfig = useMemo(
    () => configFromServer(config?.deepLinks),
    [config],
  )
  const periodoValue = useMemo(
    () => ({ periodo, setPeriodo }),
    [periodo],
  )
  const toastValue = useMemo(() => ({ push: toasts.push }), [toasts.push])

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg">
        <p className="text-meta text-muted-strong">Cargando el panel…</p>
      </div>
    )
  }

  if (error !== null || user === undefined) {
    const mensaje =
      error instanceof ApiError
        ? error.message
        : 'No pudimos verificar tu acceso. Probá recargar la página.'

    return (
      <div className="flex min-h-screen items-center justify-center bg-bg p-6">
        <Card padding="lg" elevada severidad="critical" className="max-w-md">
          <h1 className="mb-1.5 inline-flex items-center gap-2 text-titulo text-text">
            <Icon name="critico" size={15} className="text-danger-text" />
            Sin acceso al panel
          </h1>
          <p className="text-meta text-muted-strong">{mensaje}</p>
        </Card>
      </div>
    )
  }

  return (
    <DeepLinkConfigContext.Provider value={deepLinkConfig}>
      <PeriodoContext.Provider value={periodoValue}>
        <ToastContext.Provider value={toastValue}>
          <div className="flex min-h-screen bg-bg text-text">
            <Sidebar rol={user.rol} conteos={conteos} />
            <div className="flex min-w-0 flex-1 flex-col">
              <Header
                user={user}
                periodo={periodo}
                periodos={periodos}
                onPeriodoChange={setPeriodo}
                titulo={titulo}
                subtitulo={subtitulo}
              />
              {/*
                Los dos los prende el servidor, no una constante del front. El de
                acceso temporal va primero porque es el más grave: dice que la
                puerta definitiva —Cloudflare Access— todavía no está puesta.
              */}
              <ShareModeBanner visible={config?.authMode === 'share'} />
              <DemoDataBanner visible={config?.demoData ?? false} />
              <main className="min-w-0 flex-1 overflow-auto p-5">
                <div className="max-w-vista">{children}</div>
              </main>
            </div>
          </div>
          <ToastZone mensajes={toasts.mensajes} onDismiss={toasts.dismiss} />
        </ToastContext.Provider>
      </PeriodoContext.Provider>
    </DeepLinkConfigContext.Provider>
  )
}
